import * as S from './FormSearchSwapsOrReturns.styles'
import { useSelector } from 'src/utils/store'
import { defaultFulfillmentCenterTypeFilters } from 'src/features/fulfillmentCenterFilters'
import { selectFulfillmentCenters } from 'src/features/selector'
import { Box } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useEffect, useMemo, useState } from 'react'
import { actions as appActions } from '../../../state'
import { actions } from '../../SwapOrReturnState'
import { useDispatch } from 'react-redux'
import { handleOnKeyDown } from 'src/utils/Form'
import { GetSwapsOrReturnsPayload, GetSwapsOrReturnsStatusEnum, SwapOrReturnTypesEnum } from '../../SwapOrReturnTypes'
import MultipleSelect from 'src/components/MultipleSelect/MultipleSelect'
import { getEnumKeyByValue } from 'src/utils/enum'
import useMediaQuery from '@mui/material/useMediaQuery'

export const FormSearchSwapsOrReturns = () => {
  const dispatch = useDispatch()

  const { swapOrReturnSearchData, pagination } = useSelector((state) => state.swapOrReturn)

  const defaultValues = useMemo(() => ({
    fulfillmentCenterId: undefined,
    supplierName: '',
    invoice: '',
    typesIds: [],
    status: [],
  }), [])

  const {
    register, handleSubmit, watch, formState: { errors }, setValue, reset,
  } = useForm<GetSwapsOrReturnsPayload>({
    defaultValues,
  })

  const [typesFocused, setTypesFocused] = useState(false)
  const [statusFocused, setStatusFocused] = useState(false)

  const fulfillmentCenterId = watch('fulfillmentCenterId')
  const typesIds = watch('typesIds')
  const status = watch('status')

  const hasMinWidth = useMediaQuery('(min-width: 800px)')

  const fulfillmentCenters = useSelector(selectFulfillmentCenters)

  const fulfillmentCenterOptions = useMemo(() => fulfillmentCenters.map((fulfillmentCenter) => ({
    id: fulfillmentCenter.id,
    description: fulfillmentCenter.name,
  })), [fulfillmentCenters])

  const typeOptions = useMemo(() => [
    { id: 2, description: SwapOrReturnTypesEnum.SWAP },
    { id: 1, description: SwapOrReturnTypesEnum.RETURN },
  ], [])

  const statusOptions = useMemo(() => {
    const values = Object.values(GetSwapsOrReturnsStatusEnum)
    return values.map((value, index) => ({ id: index + 1, description: value }))
  }, [])

  const defaultStatus = useMemo(() => swapOrReturnSearchData?.status
    ?.map((statusKey) => GetSwapsOrReturnsStatusEnum[statusKey as keyof typeof GetSwapsOrReturnsStatusEnum])
    ?.filter(Boolean) || [], [swapOrReturnSearchData])

  const defaultTypes = useMemo(() => swapOrReturnSearchData?.typesIds || [], [swapOrReturnSearchData])

  useEffect(() => {
    dispatch(appActions.getAvailableFulfillmentCenters(defaultFulfillmentCenterTypeFilters))
  }, [dispatch])

  useEffect(() => {
    if (!swapOrReturnSearchData) {
      reset({
        fulfillmentCenterId: undefined,
        supplierName: '',
        invoice: '',
        typesIds: [],
        status: [],
      })
    } else {
      if (swapOrReturnSearchData.fulfillmentCenterId) {
        setValue('fulfillmentCenterId', swapOrReturnSearchData.fulfillmentCenterId)
      }
      if (swapOrReturnSearchData.supplierName) {
        setValue('supplierName', swapOrReturnSearchData.supplierName)
      }
      if (swapOrReturnSearchData.invoice) {
        setValue('invoice', swapOrReturnSearchData.invoice)
      }
      if (swapOrReturnSearchData.typesIds?.length) {
        setValue('typesIds', swapOrReturnSearchData.typesIds)
      }
      if (swapOrReturnSearchData.status?.length) {
        const statusValues = swapOrReturnSearchData.status
          .map((statusKey) => GetSwapsOrReturnsStatusEnum[statusKey as keyof typeof GetSwapsOrReturnsStatusEnum])
          .filter(Boolean)
        setValue('status', statusValues)
      }
    }
  }, [swapOrReturnSearchData, reset, setValue])

  const onSubmit = (formData: GetSwapsOrReturnsPayload) => {
    const statusAux = formData.status?.map((value) => getEnumKeyByValue(GetSwapsOrReturnsStatusEnum, value))
    dispatch(actions.getSwapsOrReturns({
      ...formData,
      page: 1,
      perPage: pagination.perPage,
      status: statusAux,
    }))
  }

  return (
    <Box
      component='form'
      onSubmit={handleSubmit(onSubmit)}
      sx={hasMinWidth ? {
        display: 'grid',
        gridTemplateColumns: 'auto 12rem',
        gap: '1rem',
        width: '100%',
        margin: '1.3rem 0 1.3rem 0',
      } : {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        width: '100%',
        margin: '1.3rem 0 1.3rem 0rem',
        paddingRight: '.8rem',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '1rem',
          width: '100%',
        }}
      >
        <Box
          sx={hasMinWidth ? {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, min(9.1rem), auto)',
            gridAutoFlow: 'column',
            gap: '1rem',
            width: '100%',
          } : {
            display: 'flex',
            flexDirection: 'column',
            flexWrap: 'wrap',
            gap: '1rem',
            width: '100%',
          }}
        >
          <S.CustomSelect
            name='fulfillmentCenterId'
            value={fulfillmentCenterId || ''}
            inputProps={{
              ...register(
                'fulfillmentCenterId',
                { required: 'Campo obrigatório' },
              ),
              'data-testid': 'input-fulfillment-center-select-test-id',
            }}
            autoComplete='off'
            id='fulfillmentCenterId'
            label='Selecione um FC'
            variant='outlined'
            options={fulfillmentCenterOptions}
            error={!!errors.fulfillmentCenterId}
            helperText={errors.fulfillmentCenterId?.message}
            optionNone={false}
            fullWidth
            type='text'
            autoFocus
            maxHeight='600px'
            dataTestId='fulfillment-center-select-test-id'
          />
          <MultipleSelect
            register={register}
            inputName='typesIds'
            id='typeId'
            label='Tipo'
            variant='outlined'
            options={typeOptions}
            errors={errors.typesIds}
            type='text'
            maxHeight='600px'
            optionProperty='id'
            fullWidth
            disabled={!fulfillmentCenterId}
            dataTestId='type-id-multiple-select-test-id'
            defaultOptions={defaultTypes}
            onFocus={() => setTypesFocused(true)}
            onBlur={() => setTypesFocused(false)}
            customInputLabelProps={{
              shrink: (typesIds && typesIds.length > 0) || typesFocused,
            }}
          />
          <MultipleSelect
            key={`status-${defaultStatus.length}-${defaultStatus.join('-')}`}
            register={register}
            inputName='status'
            id='status'
            label='Status'
            variant='outlined'
            options={statusOptions}
            errors={errors.status}
            type='text'
            maxHeight='600px'
            fullWidth
            disabled={!fulfillmentCenterId}
            dataTestId='status-multiple-select-test-id'
            defaultOptions={defaultStatus}
            onFocus={() => setStatusFocused(true)}
            onBlur={() => setStatusFocused(false)}
            customInputLabelProps={{
              shrink: (status && status.length > 0) || statusFocused,
            }}
          />
        </Box>
        <Box
          sx={hasMinWidth ? {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, auto)',
            gap: '1rem',
            width: '100%',
          } : {
            display: 'flex',
            flexDirection: 'column',
            flexWrap: 'wrap',
            gap: '1rem',
            width: '100%',
          }}
        >
          <S.CustomInputText
            inputProps={{
              ...register('supplierName'),
              'data-testid': 'supplier-name-input-test-id',
            }}
            value={watch('supplierName') || ''}
            id='supplierName'
            name='supplierName'
            testId='supplier-name-input-test-id'
            label='Nome do Fornecedor'
            type='text'
            autoComplete='off'
            disabled={!fulfillmentCenterId}
            onKeyDown={(e) => handleOnKeyDown(e, 'invoice')}
          />
          <S.CustomInputText
            inputProps={{
              ...register('invoice'),
              'data-testid': 'invoice-input-test-id',
            }}
            id='invoice'
            name='invoice'
            testId='invoice-input-test-id'
            label='Número ou Chave da Nota Fiscal'
            type='text'
            autoComplete='off'
            disabled={!fulfillmentCenterId}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
        }}
      >
        <S.CustomButton
          title='Buscar'
          type='submit'
          bgColor='blue'
          hoverColor='darkBlueHover'
          letterColor='white'
          data-testid='search-swaps-or-returns-button-test-id'
        />
      </Box>
    </Box>
  )
}
